import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, 
  UserPlus, 
  Trash2, 
  Loader2,
  RefreshCw,
  Mail,
  User,
  Lock,
  Eye,
  EyeOff,
  Crown
} from 'lucide-react';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * AdminMasters - Gerenciar usuários master/admin
 */
const AdminMasters = () => {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: ''
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API}/api/admin/masters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data.admins || []);
    } catch (error) {
      console.error('Erro ao carregar admins:', error);
      toast.error('Erro ao carregar administradores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setCreating(true);
      const token = getToken();
      await axios.post(`${API}/api/admin/create-master`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Usuário master criado com sucesso!');
      setShowForm(false);
      setFormData({ email: '', name: '', password: '' });
      loadAdmins();
    } catch (error) {
      console.error('Erro ao criar admin:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar administrador');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (adminId, adminName) => {
    if (!confirm(`Tem certeza que deseja remover "${adminName}" como administrador?`)) {
      return;
    }

    try {
      const token = getToken();
      await axios.delete(`${API}/api/admin/masters/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Administrador removido');
      loadAdmins();
    } catch (error) {
      console.error('Erro ao remover admin:', error);
      toast.error(error.response?.data?.detail || 'Erro ao remover administrador');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Crown className="w-7 h-7 text-[#C79F5E]" />
            Usuários Master
          </h2>
          <p className="text-gray-400 text-sm">Gerenciar administradores do sistema</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadAdmins}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black font-semibold rounded-lg hover:opacity-90 transition-all"
          >
            <UserPlus className="w-4 h-4" />
            Criar Master
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/30 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#C79F5E]" />
            Criar Novo Administrador
          </h3>
          
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nome</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                    placeholder="Nome do administrador"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-12 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                  placeholder="Mínimo 6 caracteres"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setFormData({ email: '', name: '', password: '' }); }}
                className="flex-1 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex-1 py-3 bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
              >
                {creating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Criar Administrador'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Admins List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#C79F5E] animate-spin" />
        </div>
      ) : admins.length === 0 ? (
        <div className="text-center py-12 bg-white/5 rounded-xl">
          <Shield className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">Nenhum administrador encontrado</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {admins.map((admin) => (
            <div
              key={admin.id}
              className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-[#C79F5E]/30 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C79F5E] to-[#8B6914] flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold flex items-center gap-2">
                    {admin.name || 'Admin'}
                    <span className="px-2 py-0.5 bg-[#C79F5E]/20 text-[#C79F5E] text-xs rounded-full">
                      Master
                    </span>
                  </p>
                  <p className="text-sm text-gray-400">{admin.email}</p>
                  <p className="text-xs text-gray-500">Desde: {formatDate(admin.created_at)}</p>
                </div>
              </div>
              
              <button
                onClick={() => handleDelete(admin.id, admin.name || admin.email)}
                className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400 transition-all"
                title="Remover administrador"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <p className="text-blue-300 text-sm">
          <strong>Dica:</strong> Usuários master têm acesso completo ao painel administrativo, 
          incluindo gestão de criadores, saques, verificações e moderação.
        </p>
      </div>
    </div>
  );
};

export default AdminMasters;
